/* CATALOG DESIGNER — Seller-side control panel preview (Screen 1).
 * Dark mode admin control with side-by-side live preview pane.
 * Mini-Linesheet shown in the right pane. */
import { useState } from "react";
import { useCatalogFonts } from "./_fonts";
import { PreviewRibbon } from "./LinesheetPreview";
import {
  Sliders, Grid3x3, Sparkles, Table, Gem, Check,
  Monitor, Smartphone, RotateCcw, Send,
  Type, Square, Maximize2, Plus, Trash2,
  Search, ShoppingCart, ChevronDown, ChevronRight,
} from "lucide-react";

const C = {
  bg:       "#0a1f1c",
  surface:  "#122a27",
  surface2: "#1a3431",
  primary:  "#e8ff3a",
  text:     "#ffffff",
  muted:    "#a1b3b1",
  border:   "#213835",
  dim:      "#6b827f",
};

const TEMPLATES = [
  { id: "linesheet",     name: "Linesheet",     desc: "Default · many SKUs", icon: Grid3x3 },
  { id: "editorial",     name: "Editorial",     desc: "Premium · lifestyle",  icon: Sparkles },
  { id: "catalog_sheet", name: "Catalog Sheet", desc: "B2B · data-dense",     icon: Table },
  { id: "showcase",      name: "Showcase",      desc: "Artisanal · curated",  icon: Gem },
];

const FONT_PRESETS = [
  { id: "modern",     label: "Modern",     pair: "Cabinet Grotesk · Satoshi" },
  { id: "editorial",  label: "Editorial",  pair: "Playfair Display · Outfit" },
  { id: "industrial", label: "Industrial", pair: "Chivo · Roboto Mono" },
];

const CARD_STYLES = [
  { id: "sharp",   label: "Sharp" },
  { id: "rounded", label: "Rounded" },
  { id: "soft",    label: "Soft shadow" },
];

const DENSITIES = [
  { id: "compact",     label: "Compact" },
  { id: "comfortable", label: "Comfortable" },
  { id: "spacious",    label: "Spacious" },
];

const MODULES = [
  { id: "featured_collections", label: "Featured collections",  desc: "Banner strip on top of the catalog" },
  { id: "bestsellers_row",      label: "Bestsellers row",       desc: "Horizontal scroll of top sellers" },
  { id: "new_arrivals_row",     label: "New arrivals row",      desc: "Horizontal scroll of recent SKUs" },
  { id: "quick_order",          label: "Quick-order paste box", desc: "Bulk SKU,qty input for power buyers" },
  { id: "about_brand",          label: "About-brand strip",     desc: "Editorial section with your story" },
  { id: "press_certifications", label: "Press / certifications", desc: "Logo strip below the hero" },
];

export default function CatalogDesignerPreview() {
  useCatalogFonts();
  const [tpl, setTpl] = useState("linesheet");
  const [primary, setPrimary] = useState("#849C8A");
  const [accent, setAccent] = useState("#1A2E22");
  const [font, setFont] = useState("modern");
  const [card, setCard] = useState("rounded");
  const [density, setDensity] = useState("comfortable");
  const [device, setDevice] = useState("desktop");
  const [activated, setActivated] = useState(true);
  const [modules, setModules] = useState({
    featured_collections: true,
    bestsellers_row: true,
    new_arrivals_row: true,
    quick_order: true,
    about_brand: false,
    press_certifications: false,
  });

  const toggleMod = (id) => setModules((m) => ({ ...m, [id]: !m[id] }));

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Satoshi', sans-serif", minHeight: "100vh" }} data-testid="designer-preview">
      <PreviewRibbon name="Catalog Designer"/>

      {/* TOP BAR */}
      <div className="sticky top-9 z-20 px-6 h-16 flex items-center justify-between" style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-lg" style={{ background: C.primary, color: C.bg }}>
            <Sliders className="w-4 h-4"/>
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: C.dim, fontFamily: "'JetBrains Mono', monospace" }}>Seller · settings</div>
            <div className="text-[14px]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 600 }}>Catalog Designer</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="h-9 px-3.5 rounded-full text-xs inline-flex items-center gap-1.5" style={{ color: C.muted, background: "transparent" }}>
            <RotateCcw className="w-3 h-3"/>Discard changes
          </button>
          <button className="h-9 px-4 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: C.primary, color: C.bg }} data-testid="publish-btn">
            <Send className="w-3 h-3"/>Publish
          </button>
        </div>
      </div>

      {/* SPLIT VIEW */}
      <div className="grid lg:grid-cols-[380px_1fr]" style={{ height: "calc(100vh - 100px)" }}>
        {/* LEFT PANEL */}
        <aside className="overflow-y-auto" style={{ borderRight: `1px solid ${C.border}` }}>
          <div className="p-6 space-y-7">
            {/* TEMPLATE */}
            <Section label="Template" hint="Pick the layout. Switchable any time.">
              <div className="grid grid-cols-2 gap-2.5">
                {TEMPLATES.map((t) => {
                  const active = tpl === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTpl(t.id)}
                      className="text-left rounded-lg p-3 relative transition-colors"
                      style={{
                        background: active ? C.surface2 : C.surface,
                        border: `1px solid ${active ? C.primary : C.border}`,
                      }}
                      data-testid={`tpl-${t.id}`}
                    >
                      {active && (
                        <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full grid place-items-center" style={{ background: C.primary, color: C.bg }}>
                          <Check className="w-2.5 h-2.5"/>
                        </span>
                      )}
                      <t.icon className="w-4 h-4" style={{ color: active ? C.primary : C.muted }}/>
                      <div className="mt-2 text-[13px] font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t.name}</div>
                      <div className="text-[10.5px] mt-0.5" style={{ color: C.dim, fontFamily: "'JetBrains Mono', monospace" }}>{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* THEME */}
            <Section label="Theme">
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                <ColorField label="Primary" value={primary} onChange={setPrimary}/>
                <ColorField label="Accent"  value={accent}  onChange={setAccent}/>
              </div>

              <Field label="Font preset">
                <div className="space-y-1.5">
                  {FONT_PRESETS.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFont(f.id)}
                      className="w-full text-left rounded-md p-2.5 flex items-center justify-between transition-colors"
                      style={{
                        background: font === f.id ? C.surface2 : C.surface,
                        border: `1px solid ${font === f.id ? C.primary : C.border}`,
                      }}
                    >
                      <div>
                        <div className="text-[12.5px] font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{f.label}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: C.dim, fontFamily: "'JetBrains Mono', monospace" }}>{f.pair}</div>
                      </div>
                      {font === f.id && <Check className="w-3.5 h-3.5" style={{ color: C.primary }}/>}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Card style">
                <Segmented options={CARD_STYLES} value={card} onChange={setCard}/>
              </Field>

              <Field label="Density">
                <Segmented options={DENSITIES} value={density} onChange={setDensity}/>
              </Field>
            </Section>

            {/* MODULES */}
            <Section label="Modules" hint="Show / hide content blocks">
              <div className="space-y-2">
                {MODULES.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => toggleMod(m.id)}
                    className="w-full rounded-lg p-3 flex items-start gap-3 text-left transition-colors"
                    style={{ background: C.surface, border: `1px solid ${C.border}` }}
                    data-testid={`mod-${m.id}`}
                  >
                    <Toggle on={modules[m.id]}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 500 }}>{m.label}</div>
                      <div className="text-[10.5px] mt-0.5" style={{ color: C.dim }}>{m.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </Section>

            {/* CONTENT */}
            <Section label="Content">
              <Field label="About-brand blurb">
                <textarea
                  rows={4}
                  defaultValue="Estate-pressed olive oils and aged vinegars sourced from single-origin growers across the Mediterranean."
                  className="w-full rounded-md p-3 text-[12px] focus:outline-none"
                  style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text }}
                />
              </Field>

              <Field label="Press logo URLs">
                <div className="space-y-1.5">
                  {["Bon Appétit", "Eater", "Cherry Bombe"].map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        defaultValue={`https://cdn.../${p.toLowerCase().replace(/\s/g, "-")}.svg`}
                        className="flex-1 h-8 rounded-md px-3 text-[11.5px] focus:outline-none"
                        style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}
                      />
                      <button className="w-8 h-8 grid place-items-center rounded-md" style={{ color: C.dim, background: C.surface, border: `1px solid ${C.border}` }}>
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </div>
                  ))}
                  <button className="w-full h-8 rounded-md text-[11px] inline-flex items-center justify-center gap-1.5" style={{ color: C.muted, border: `1px dashed ${C.border}` }}>
                    <Plus className="w-3 h-3"/>Add logo
                  </button>
                </div>
              </Field>
            </Section>

            {/* ACTIVATE */}
            <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: C.surface2, border: `1px solid ${C.border}` }}>
              <Toggle on={activated} onClick={() => setActivated(!activated)} primary/>
              <div className="flex-1">
                <div className="text-[13px]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 600 }}>Activate Catalog app for buyers</div>
                <div className="text-[10.5px] mt-1 leading-relaxed" style={{ color: C.muted }}>
                  When ON, your Catalog appears in the buyer&apos;s app drawer. Toggle OFF to hide while you redesign.
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* RIGHT — LIVE PREVIEW */}
        <main className="overflow-y-auto" style={{ background: "#070f0e" }}>
          <div className="sticky top-0 z-10 px-6 h-14 flex items-center justify-between" style={{ background: "#070f0e", borderBottom: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: C.dim, fontFamily: "'JetBrains Mono', monospace" }}>Live preview</div>
              <span className="px-2 py-0.5 rounded text-[9.5px] uppercase tracking-wider" style={{ background: C.primary, color: C.bg, fontFamily: "'JetBrains Mono', monospace" }}>
                {tpl.replace("_", " ")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-full p-0.5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <button onClick={() => setDevice("desktop")} className="h-7 px-3 rounded-full inline-flex items-center gap-1.5 text-[11px]" style={{ background: device === "desktop" ? C.primary : "transparent", color: device === "desktop" ? C.bg : C.muted }}>
                  <Monitor className="w-3 h-3"/>Desktop
                </button>
                <button onClick={() => setDevice("mobile")} className="h-7 px-3 rounded-full inline-flex items-center gap-1.5 text-[11px]" style={{ background: device === "mobile" ? C.primary : "transparent", color: device === "mobile" ? C.bg : C.muted }}>
                  <Smartphone className="w-3 h-3"/>Mobile
                </button>
              </div>
              <a href="/preview/catalog/linesheet" target="_blank" rel="noreferrer" className="h-8 px-3 rounded-full text-[11px] inline-flex items-center gap-1.5" style={{ color: C.muted, border: `1px solid ${C.border}` }}>
                Full preview <ChevronRight className="w-3 h-3"/>
              </a>
            </div>
          </div>

          <div className="p-6 flex justify-center">
            <div
              className="rounded-xl overflow-hidden shadow-2xl"
              style={{
                width: device === "mobile" ? 380 : "100%",
                maxWidth: device === "desktop" ? 1100 : 380,
                background: "#F9F6F0",
                border: `1px solid ${C.border}`,
              }}
            >
              {/* Browser chrome */}
              <div className="h-7 flex items-center gap-1.5 px-3" style={{ background: "#E5DDD0", borderBottom: "1px solid #D5CCBC" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#E76F51" }}/>
                <span className="w-2 h-2 rounded-full" style={{ background: "#F4A261" }}/>
                <span className="w-2 h-2 rounded-full" style={{ background: "#849C8A" }}/>
                <div className="ml-3 text-[9.5px]" style={{ color: "#8B7A60", fontFamily: "'JetBrains Mono', monospace" }}>flora-botanica.verqo.com/catalog</div>
              </div>
              <MiniLinesheet primary={primary} accent={accent} card={card} density={density} mobile={device === "mobile"} modules={modules}/>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ─────────── Mini Linesheet (live preview content) ─────────── */
function MiniLinesheet({ primary, accent, card, density, mobile, modules }) {
  const radius = card === "sharp" ? 4 : card === "rounded" ? 14 : 18;
  const shadow = card === "soft" ? "0 10px 30px rgba(0,0,0,0.06)" : "none";
  const pad = density === "compact" ? 10 : density === "spacious" ? 20 : 14;

  const products = [
    { name: "Lavender Calm Serum", price: 18.5, moq: 6,  img: "https://images.unsplash.com/photo-1739980213756-753aea153bb8?w=400" },
    { name: "Rose Hydra Mist",     price: 12.0, moq: 12, img: "https://images.unsplash.com/photo-1608571424266-edeb9bbefdec?w=400" },
    { name: "Glow Body Oil",       price: 14.75,moq: 6,  img: "https://images.unsplash.com/photo-1739980104488-408eff709fff?w=400" },
    { name: "Clay Mask",           price: 9.20, moq: 24, img: "https://images.unsplash.com/photo-1608571424266-edeb9bbefdec?w=400" },
    { name: "Rosehip Oil",         price: 22.4, moq: 6,  img: "https://images.unsplash.com/photo-1739980213756-753aea153bb8?w=400" },
    { name: "Aloe Gel",            price: 8.90, moq: 12, img: "https://images.unsplash.com/photo-1739980104488-408eff709fff?w=400" },
  ];

  return (
    <div style={{ background: "#F9F6F0", color: "#1A2E22", fontFamily: "'IBM Plex Sans', sans-serif", padding: mobile ? 14 : 22 }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold" style={{ background: primary, color: "#fff" }}>F</span>
          <div className="text-[12px] font-medium" style={{ fontFamily: "'Work Sans', sans-serif" }}>Flora Botanica</div>
        </div>
        <button className="h-7 px-3 rounded-full text-[10.5px] inline-flex items-center gap-1" style={{ background: accent, color: "#F9F6F0" }}>
          <ShoppingCart className="w-3 h-3"/>Cart · 3
        </button>
      </div>

      <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: "#5C7364", fontFamily: "'JetBrains Mono', monospace" }}>Apps · Catalog</div>
      <h2 className="mt-1" style={{ fontSize: mobile ? 22 : 28, fontFamily: "'Work Sans', sans-serif", fontWeight: 600, letterSpacing: "-0.01em" }}>Shop catalog</h2>

      {/* Search */}
      <div className="relative mt-3 mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: "#5C7364" }}/>
        <input placeholder="Search products, SKU…" className="w-full h-8 rounded-full pl-7 pr-3 text-[11px]" style={{ background: "#fff", border: "1px solid #E5DDD0" }}/>
      </div>

      {/* Modules */}
      {modules.featured_collections && (
        <div className="mb-3 rounded-md p-2.5 flex items-center gap-2" style={{ background: "#fff", border: `1px solid ${primary}33` }}>
          <span className="text-[8.5px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: primary, color: "#fff", fontFamily: "'JetBrains Mono', monospace" }}>Featured</span>
          <div className="text-[10.5px]" style={{ color: "#1A2E22" }}>Winter Calm collection · 12 SKUs</div>
        </div>
      )}

      {modules.bestsellers_row && (
        <div className="mb-3">
          <div className="text-[9px] uppercase tracking-[0.22em] mb-1.5" style={{ color: "#5C7364", fontFamily: "'JetBrains Mono', monospace" }}>Bestsellers</div>
          <div className="flex gap-2 overflow-hidden">
            {products.slice(0, 4).map((p, i) => (
              <div key={i} className="shrink-0 w-20" style={{ borderRadius: radius, background: "#fff", overflow: "hidden", boxShadow: shadow, border: "1px solid #E5DDD0" }}>
                <img src={p.img} alt="" className="w-full h-16 object-cover"/>
                <div style={{ padding: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}>${p.price.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product grid */}
      <div className={`grid ${mobile ? "grid-cols-2" : "grid-cols-3"}`} style={{ gap: pad - 4 }}>
        {products.map((p) => (
          <div key={p.name} style={{ background: "#fff", borderRadius: radius, overflow: "hidden", boxShadow: shadow, border: "1px solid #E5DDD0" }}>
            <div style={{ aspectRatio: "1/1", background: "#EFEAE0" }}>
              <img src={p.img} alt="" className="w-full h-full object-cover"/>
            </div>
            <div style={{ padding: pad }}>
              <div style={{ fontSize: 11, fontFamily: "'Work Sans', sans-serif", fontWeight: 500, lineHeight: 1.2 }}>{p.name}</div>
              <div className="mt-1.5 flex items-end justify-between">
                <div style={{ fontSize: 14, fontWeight: 600, fontFamily: "'Work Sans', sans-serif" }}>${p.price.toFixed(2)}</div>
                <div className="text-[8.5px]" style={{ color: "#5C7364" }}>MOQ {p.moq}</div>
              </div>
              <button className="mt-2 w-full text-[10px] py-1.5 rounded-full font-medium" style={{ background: accent, color: "#F9F6F0", fontFamily: "'Work Sans', sans-serif" }}>
                Add to order
              </button>
            </div>
          </div>
        ))}
      </div>

      {modules.about_brand && (
        <div className="mt-5 rounded-md p-3" style={{ background: "#fff", border: "1px solid #E5DDD0" }}>
          <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: "#5C7364", fontFamily: "'JetBrains Mono', monospace" }}>Our story</div>
          <div className="text-[11px] mt-1.5 leading-relaxed" style={{ color: "#5C7364" }}>
            Estate-pressed olive oils and aged vinegars sourced from single-origin growers…
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Left-panel atoms ─────────── */
function Section({ label, hint, children }) {
  return (
    <div>
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: C.dim, fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
        {hint && <div className="text-[10.5px] mt-1" style={{ color: C.muted }}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3.5">
      <div className="text-[10.5px] mb-1.5" style={{ color: C.muted }}>{label}</div>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <div className="text-[10.5px] mb-1.5" style={{ color: C.muted }}>{label}</div>
      <label className="flex items-center gap-2 rounded-md p-2 cursor-pointer" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        <span className="w-7 h-7 rounded shrink-0" style={{ background: value, border: "1px solid rgba(255,255,255,0.2)" }}/>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent text-[11.5px] focus:outline-none"
          style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}
        />
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-0 h-0 opacity-0 absolute"/>
      </label>
    </div>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-md p-0.5 w-full" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className="flex-1 h-7 rounded text-[11px] transition-colors"
          style={{
            background: value === o.id ? C.primary : "transparent",
            color: value === o.id ? C.bg : C.muted,
            fontWeight: value === o.id ? 600 : 400,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, onClick, primary }) {
  const color = primary && on ? "#e8ff3a" : on ? "#a1b3b1" : C.border;
  return (
    <button
      onClick={onClick}
      className="shrink-0 w-9 h-5 rounded-full p-0.5 flex items-center transition-colors"
      style={{ background: on ? color : C.surface2, border: `1px solid ${C.border}` }}
    >
      <span
        className="w-4 h-4 rounded-full transition-transform"
        style={{ background: on ? (primary ? C.bg : C.text) : C.dim, transform: on ? "translateX(14px)" : "translateX(0)" }}
      />
    </button>
  );
}
